import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';
import get from 'lodash.get';
import { updateUriRef } from '../../../actions/filterActions';
import { formatRange, getInputRangeMinMax } from '../sbSharedRefineMenuHelpers';

// components
import { Button } from 'dibs-elements/exports/Button';
import ArrowRight from 'dibs-icons/exports/ArrowRight';
import { SbSharedLabeledInput } from '../../SbSharedLabeledInput/SbSharedLabeledInput';

// styles
import styles from './SbSharedRefineMenuInputRange.scss';

export class SbSharedRefineMenuInputRangeComponent extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            minValue: Number(props.min) ? Math.floor(parseInt(props.min)).toString() : null,
            maxValue: Number(props.max) ? Math.ceil(parseInt(props.max)).toString() : null,
            error: false,
            showAnimatedPlaceholderMin: props.showAnimatedPlaceholder,
            showAnimatedPlaceholderMax: props.showAnimatedPlaceholder,
        };

        this.setValue = this.setValue.bind(this);
        this.applyValue = this.applyValue.bind(this);
    }

    componentDidUpdate(prevProps, prevState) {
        const { min, max, showAnimatedPlaceholder } = this.props;
        const { minValue, maxValue } = this.state;

        const newState = {};
        if (prevProps.showAnimatedPlaceholder !== showAnimatedPlaceholder) {
            newState.showAnimatedPlaceholderMin = showAnimatedPlaceholder;
            newState.showAnimatedPlaceholderMax = showAnimatedPlaceholder;
            newState.maxValue = Math.ceil(parseInt(max)).toString();
            newState.minValue = Math.floor(parseInt(min)).toString();
        }

        if (prevProps.max !== max) {
            newState.maxValue = Math.ceil(parseInt(max)).toString();
        }

        if (prevProps.min !== min) {
            newState.minValue = Math.floor(parseInt(min)).toString();
        }

        // Checking both props and state validity, because both changes can invoke component update
        // depending on how range values where provided: by chosing predefined range or manually filling fields
        const error = this.isInvalidRange(min, max) || this.isInvalidRange(minValue, maxValue);
        if (prevState.error !== error) {
            newState.error = error;
        }

        if (Object.keys(newState).length > 0) {
            this.setState(newState);
        }
    }

    isInvalidRange(min, max) {
        return parseInt(min) > parseInt(max) || parseInt(max) <= 0;
    }

    setValue(name, value, e) {
        if (e && typeof e.preventDefault === 'function') {
            e.preventDefault();
        }
        const { availableMin, availableMax, applyOnBlur } = this.props;
        let { minValue, maxValue } = this.state;
        let shouldApply;

        if (name === 'min') {
            value = parseInt(value) < parseInt(availableMin) ? availableMin : value;
            shouldApply = value !== minValue;
            minValue = value;
            this.setState({ minValue, showAnimatedPlaceholderMin: false });
        } else {
            value = parseInt(value) > parseInt(availableMax) ? availableMax : value;
            shouldApply = value !== maxValue;
            maxValue = value;
            this.setState({ maxValue, showAnimatedPlaceholderMax: false });
        }

        const isInvalidRange = this.isInvalidRange(minValue, maxValue);

        if (!isInvalidRange && shouldApply && applyOnBlur) {
            // only apply if
            // 1. there is no validation error
            // 2. the value of min or max has changed
            // 3. component is set to apply values on blur
            this.props.onApply([minValue, maxValue]);
        }

        if (this.state.error !== isInvalidRange) {
            this.setState({ error: isInvalidRange });
        }
    }

    applyValue(e) {
        if (e && typeof e.preventDefault === 'function') {
            e.preventDefault();
        }
        const { minValue, maxValue, error } = this.state;

        if (!error) {
            this.props.onApply([minValue, maxValue]);
            this.setState({ showAnimatedPlaceholderMin: false, showAnimatedPlaceholderMax: false });
        }
    }

    render() {
        const {
            labelFrom,
            labelTo,
            applyOnBlur,
            showInvalidRangeErrorMessage,
            size,
            refetchInFlight,
        } = this.props;
        const { showAnimatedPlaceholderMin, showAnimatedPlaceholderMax } = this.state;

        return (
            <React.Fragment>
                <div className={styles.container}>
                    <SbSharedLabeledInput
                        label={labelFrom}
                        size={size}
                        currency={this.props.currency}
                        value={this.state.minValue}
                        onBlur={(value, e) => this.setValue('min', value, e)}
                        dataTn="search-browse-currency-input-min"
                        name="price-min"
                        hasError={this.state.error}
                        showAnimatedPlaceholder={showAnimatedPlaceholderMin}
                    />
                    <div className={styles.divider}>
                        <FormattedMessage
                            id="sb.SbMobileRefineMenu.inputRange.to"
                            defaultMessage="to"
                        />
                    </div>
                    <SbSharedLabeledInput
                        label={labelTo}
                        size={size}
                        currency={this.props.currency}
                        value={this.state.maxValue}
                        onBlur={(value, e) => this.setValue('max', value, e)}
                        dataTn="search-browse-currency-input-max"
                        name="price-max"
                        hasError={this.state.error}
                        showAnimatedPlaceholder={showAnimatedPlaceholderMax}
                    />
                    {!applyOnBlur && (
                        <Button
                            type="secondary"
                            size={size || 'large'}
                            onClick={this.applyValue}
                            className={styles.inputRangeButton}
                            isDangerousAction={this.state.error}
                            dataTn={`input-range-apply${this.state.error ? '-error' : ''}`}
                            disabled={refetchInFlight}
                        >
                            <ArrowRight className={styles.arrow} />
                        </Button>
                    )}
                </div>
                {this.state.error && showInvalidRangeErrorMessage && (
                    <div className={styles.error}>
                        <FormattedMessage
                            id="sb.SbMobileRefineMenu.inputRange.error"
                            defaultMessage="Min value may not exceed max"
                        />
                    </div>
                )}
            </React.Fragment>
        );
    }
}

SbSharedRefineMenuInputRangeComponent.defaultProps = {
    onApply: () => {},
    applyOnBlur: false,
    showInvalidRangeErrorMessage: true,
};

SbSharedRefineMenuInputRangeComponent.propTypes = {
    onApply: PropTypes.func,
    labelTo: PropTypes.node,
    labelFrom: PropTypes.node,
    filterName: PropTypes.string,
    additionalFilter: PropTypes.string,
    statKey: PropTypes.string,
    currency: PropTypes.string,
    applyOnBlur: PropTypes.bool,
    updateOnBlur: PropTypes.bool,
    showInvalidRangeErrorMessage: PropTypes.bool,
    showAnimatedPlaceholder: PropTypes.bool,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    max: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    min: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    availableMin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    availableMax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    refetchInFlight: PropTypes.bool,
};

function mapStateToProps(state, props) {
    const { filter, filterName, statKey, itemSearch } = props;
    const { appliedFilters } = itemSearch;
    const refetchInFlight = state.filters.refetchInFlight || false;

    return {
        ...getInputRangeMinMax({ filter, filterName, statKey, appliedFilters }),
        refetchInFlight,
    };
}

function mapDispatchToProps(dispatch, { filterName, filter, additionalFilter, statKey }) {
    return {
        onApply(minMax) {
            let uriRef = formatRange(get(filter, 'values[0].linkReference'), minMax);
            // Add `currency` query param for `price` and `measurement-unit` for dimensions params
            if (statKey && additionalFilter) {
                uriRef = uriRef.replace(`{${additionalFilter}}`, statKey.toLowerCase());
            }
            dispatch(updateUriRef({ filterName, uriRef }));
        },
    };
}

export const SbSharedRefineMenuInputRange = createFragmentContainer(
    connect(mapStateToProps, mapDispatchToProps)(SbSharedRefineMenuInputRangeComponent),
    {
        itemSearch: graphql`
            fragment SbSharedRefineMenuInputRange_itemSearch on ItemSearchQueryConnection {
                appliedFilters {
                    name
                    values {
                        urlLabel
                    }
                }
            }
        `,
    }
);
